import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import { useTranslation } from "../../../i18n";
import {
  fetchNotificationConfig,
  updateNotificationConfig,
} from "../notificationsApi";
import { useCurrentUser } from "../../../shared/components/UserProfileBadge";

const THRESHOLD_OPTIONS = [1, 2, 3, 4];

function AlertToggle({ checked, onChange, label }) {
  return (
    <label className="relative inline-flex shrink-0 cursor-pointer items-center">
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        onChange={onChange}
        aria-label={label}
      />
      <span
        className={[
          "relative h-8 w-14 rounded-full bg-outline-variant transition-colors",
          "after:absolute after:left-1 after:top-1 after:h-6 after:w-6 after:rounded-full",
          "after:border after:border-gray-300 after:bg-white after:transition-transform",
          "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2",
          "peer-focus-visible:outline-secondary peer-checked:bg-secondary",
          "peer-checked:after:translate-x-6",
        ].join(" ")}
        aria-hidden
      />
    </label>
  );
}

const AlertCard = ({ icon, iconBoxClass, iconClass, title, hint, checked, onToggle, children }) => (
  <article className="flex items-center justify-between gap-6 rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(15,23,42,0.1)] md:p-8">
    <div className="flex min-w-0 items-start gap-6">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${iconBoxClass}`}>
        <MaterialIcon name={icon} className={`text-[22px] ${iconClass}`} />
      </div>
      <div className="min-w-0">
        <h3 className="mb-1 font-headline-md text-headline-md text-primary">{title}</h3>
        <p className="font-body-md text-body-md text-on-surface-variant">{hint}</p>
        {children}
      </div>
    </div>
    <AlertToggle checked={checked} onChange={onToggle} label={title} />
  </article>
);

const ClientEmailAlertsPage = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await fetchNotificationConfig();
        if (active) setConfig(data);
      } catch {
        if (active) setError(t("emailAlerts.loadError"));
      }
    })();
    return () => {
      active = false;
    };
  }, [t]);

  const patch = useCallback((changes) => {
    setConfig((prev) => (prev ? { ...prev, ...changes } : prev));
  }, []);

  const handleSave = async () => {
    if (!config || saving) return;
    setSaving(true);
    setError("");
    try {
      const saved = await updateNotificationConfig(config);
      setConfig(saved);
      setToastVisible(true);
      window.setTimeout(() => setToastVisible(false), 3000);
    } catch {
      setError(t("emailAlerts.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const recipientEmail =
    currentUser?.company?.company_email || currentUser?.email || "";

  return (
    <main className="min-h-screen bg-background font-body-md text-on-surface antialiased">
      <div className="mx-auto max-w-container-max px-margin-mobile py-12 md:px-margin-desktop">
        <nav className="mb-stack-lg">
          <Link
            to="/dashboard/client"
            className="group inline-flex items-center gap-2 transition-colors"
          >
            <MaterialIcon
              name="arrow_back"
              className="text-[20px] text-outline transition-colors group-hover:text-primary"
            />
            <span className="font-label-sm text-label-sm text-outline transition-colors group-hover:text-primary">
              {t("common.backToDashboard")}
            </span>
          </Link>
        </nav>

        <header className="mb-12">
          <h1 className="mb-stack-sm font-headline-xl text-headline-xl text-on-background">
            {t("emailAlerts.title")}
          </h1>
          <p className="max-w-2xl font-body-lg text-body-lg text-on-surface-variant">
            {t("emailAlerts.subtitle")}
          </p>
          {recipientEmail ? (
            <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-outline-variant/40 bg-surface-container-lowest px-4 py-2 font-label-sm text-on-surface-variant">
              <MaterialIcon name="mail" className="text-[16px] text-secondary" />
              {t("emailAlerts.recipientNote", { email: recipientEmail })}
            </p>
          ) : null}
        </header>

        {error ? (
          <p className="mb-6 rounded-lg bg-error-container px-4 py-3 text-label-sm text-on-error-container">
            {error}
          </p>
        ) : null}

        {config ? (
          <div className="space-y-gutter">
            <AlertCard
              icon="report"
              iconBoxClass="bg-error-container"
              iconClass="text-error"
              title={t("emailAlerts.negativeReview")}
              hint={t("emailAlerts.negativeReviewHintDynamic", {
                threshold: config.alertThreshold,
              })}
              checked={config.notifyOnNegative}
              onToggle={() => patch({ notifyOnNegative: !config.notifyOnNegative })}
            >
              {config.notifyOnNegative ? (
                <div className="mt-3 flex items-center gap-3">
                  <span className="font-label-sm text-on-surface-variant">
                    {t("emailAlerts.thresholdLabel")}
                  </span>
                  <div className="flex rounded-full border border-outline-variant/40 bg-surface-container p-1">
                    {THRESHOLD_OPTIONS.map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => patch({ alertThreshold: value })}
                        className={`rounded-full px-3 py-1 font-label-sm font-medium transition-colors ${
                          config.alertThreshold === value
                            ? "bg-secondary text-on-secondary"
                            : "text-on-surface-variant hover:text-on-surface"
                        }`}
                      >
                        {t("emailAlerts.thresholdOption", { count: value })}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </AlertCard>

            <AlertCard
              icon="notifications"
              iconBoxClass="bg-secondary-container"
              iconClass="text-on-secondary-container"
              title={t("emailAlerts.allActivity")}
              hint={t("emailAlerts.allActivityHint")}
              checked={config.notifyOnAllActivity}
              onToggle={() =>
                patch({ notifyOnAllActivity: !config.notifyOnAllActivity })
              }
            />

            <AlertCard
              icon="gavel"
              iconBoxClass="bg-primary-container"
              iconClass="text-primary-fixed"
              title={t("emailAlerts.deletionUpdates")}
              hint={t("emailAlerts.deletionUpdatesHint")}
              checked={config.notifyOnStatusChange}
              onToggle={() =>
                patch({ notifyOnStatusChange: !config.notifyOnStatusChange })
              }
            />

            <AlertCard
              icon="support_agent"
              iconBoxClass="bg-surface-container-high"
              iconClass="text-on-surface-variant"
              title={t("emailAlerts.ticketReply")}
              hint={t("emailAlerts.ticketReplyHint")}
              checked={config.notifyOnTicketReply}
              onToggle={() =>
                patch({ notifyOnTicketReply: !config.notifyOnTicketReply })
              }
            />
          </div>
        ) : null}

        {config ? (
          <footer className="mt-12 flex justify-start">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="group flex items-center gap-3 rounded-lg bg-primary-container px-10 py-4 font-headline-md text-white shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(15,23,42,0.1)] active:scale-95 disabled:opacity-60"
            >
              {t("emailAlerts.savePreferences")}
              <MaterialIcon
                name="arrow_forward"
                className="text-[20px] transition-transform group-hover:translate-x-1"
              />
            </button>
          </footer>
        ) : null}
      </div>

      <div
        role="status"
        aria-live="polite"
        className={[
          "pointer-events-none fixed bottom-10 right-10 z-50 flex items-center gap-3 rounded-xl bg-on-secondary-container px-6 py-4 text-white shadow-xl transition-all duration-300",
          toastVisible ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0",
        ].join(" ")}
      >
        <MaterialIcon name="check_circle" className="text-[22px]" />
        <span className="font-body-md">{t("common.preferencesSaved")}</span>
      </div>
    </main>
  );
};

export default ClientEmailAlertsPage;
